import {
  LessonPlayerContextProvider,
  LessonState
} from '../models/LessonPlayerContext';
import React, { CSSProperties, useEffect, useState } from 'react';
import Slide, { SlideProps } from '@mui/material/Slide';
import { cloneDeep, isEmpty, isEqual } from 'lodash';

import AlertDialog from '../../shared/components/AlertDialog';
import { ApiPortfolioState } from '../models/ApiPortfolioState';
import AssetService from '../../shared/services/AssetService';
import AuthService from '../../shared/services/AuthService';
import Box from '@mui/material/Box';
import Config from '../../shared/config';
import Constants from '../constants';
import ContentAppBar from '../../shared/components/ContentAppBar';
import { CourseLessonVm } from '../models/LessonVm';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Unstable_Grid2';
import KeywordLocate from '../../shared/components/keyword/KeywordLocate';
import { LessonAttempt } from '../models/LessonAttempt';
import { LessonFlowDialog } from './LessonFlowDialog';
import LessonLaunchParamsV2 from '../models/LessonLaunchParamsV2';
import LessonListPanel from './LessonListPanel';
import LessonPlayerEmbedded from './LessonPlayerEmbedded';
import LessonService from '../services/LessonService';
import NoSearchResultsFound from '../../shared/components/NoSearchResultsFound';
import Overlay from '../../shared/components/Overlay';
import Pathway from '../models/Pathway';
import ProgressStatusCode from '../models/ProgressStatusCode';
import RecentLessonPanel from './RecentLessonPanel';
import { SUPPORT_MESSAGE } from '../../shared/constants';
import SearchService from '../../shared/components/keyword/SearchService';
import SearchTextField from './SearchTextField';
import SharedLessonService from '../../shared/services/LessonService';
import StatusPicker from './StatusPicker';
import styles from '../../shared/styles/CourseDialog.module.scss';
import { useCourse } from '../hooks/useCourse';
import { useLessonResults } from '../hooks/useLessonResults';
import { useLessonWindow } from '../hooks/useLessonWindow';
import { useLocation } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useUserPortfolio } from '../../externaltool/hooks/useUserPortfolio';

const Transition = React.forwardRef(
  (props: SlideProps, ref: React.Ref<unknown>) => (
    <Slide direction="right" ref={ref} {...props} />
  )
);

interface CourseDialogProps {
  open: boolean;
  pathway: Pathway;
  onClose: () => void;
}

const CourseDialog = (props: CourseDialogProps) => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProgressStatusCode[]>([]);
  const [lessonLaunchParamsV2, setLessonLaunchParamsV2] =
    useState<LessonLaunchParamsV2>(null);
  const [backgroundStyle, setBackgroundStyle] = useState<CSSProperties>();
  const [lessonState, setLessonState] = useState<LessonState>(null);
  const [courseLessons, setCourseLessons] = useState<CourseLessonVm[]>([]);
  const [lesson, setLesson] = useState<CourseLessonVm>(undefined);
  const [open, setOpen] = useState<boolean>(false);
  const [createNewAttemptError, setCreateNewAttemptError] = useState(false);
  const [nextLessonLoading, setNextLessonLoading] = useState<boolean>(false);
  const [showKeywordSearch, setShowKewordSearch] = useState<string>('');

  const { cache } = useSWRConfig();
  const userkey = AuthService.Userkey;
  const selectedLessonkey = lessonLaunchParamsV2?.lessonkey;
  const selectedLessonlayer = lessonLaunchParamsV2?.layer;
  const location = useLocation();
  const queryParams = SearchService.useQueryParams(location.search);

  const answerUrl =
    userkey && selectedLessonkey && selectedLessonlayer
      ? `${Config.API}/api/portfolio/list/answer/user/${userkey}/module/${selectedLessonkey}/layer/${selectedLessonlayer}`
      : undefined;

  const {
    course,
    loading: courseLoading,
    error: loadCourseError,
    refetchProgress
  } = useCourse(userkey, +props.pathway.pathkey);

  const {
    saveLessonResultError,
    savedLessonResult,
    savingLessonResult,
    upsertLessonResult
  } = useLessonResults(+props.pathway.pathkey);

  const { migratePortfolio, migrating } = useUserPortfolio();

  const lessons: CourseLessonVm[] = LessonService.filterLessonsBy(
    courseLessons,
    'course',
    searchText,
    statusFilter
  ) as CourseLessonVm[];

  const { handleMessage } = useLessonWindow(
    lessonLaunchParamsV2?.lessonWindow,
    handleCloseLesson
  );

  // Update all lessons and current lesson if `course` values changed.
  // This is to make sure to get latest published courseMap and portfolio results.
  useEffect(() => {
    if (course?.lessons) {
      const updatedLessons = course.lessons ?? [];
      setCourseLessons(updatedLessons);

      if (lesson) {
        const updatedLesson = updatedLessons.find(
          (l) => l.lessonkey === lesson.lessonkey
        );
        if (!isEqual(lesson, updatedLesson)) {
          setLesson(updatedLesson);
        }
      }
    }
  }, [course]);

  useEffect(() => {
    if (!lessonLaunchParamsV2) {
      return;
    }
    const { lessonkey, layer, state } = lessonLaunchParamsV2;
    setLessonState({
      answerParams: {
        userkey,
        lessonkey,
        layer
      },
      state
    });
  }, [lessonLaunchParamsV2]);

  useEffect(() => {
    if (!props.pathway) {
      return;
    }

    const displaySettings = props.pathway.displaySettings;
    if (!displaySettings) {
      return;
    }
    setBackgroundStyle({
      backgroundColor:
        displaySettings.background?.type === 'color'
          ? displaySettings.background?.value || 'ihherit'
          : 'inherit',
      backgroundImage:
        displaySettings.background?.type === 'image'
          ? `url(${AssetService.getAssetUrl(
              displaySettings.background?.value
            )})` || 'ihherit'
          : 'inherit',
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed'
    });
  }, [props.pathway]);

  useEffect(() => {
    if (savedLessonResult) {
      //* Got a response for the newly created lesson result (Portfolio).
      //* It's triggered by the call of upsertLessonResult().
      const { modulekey: lessonkey, moduleversion: lessonVersion } =
        savedLessonResult;
      const newAttempt: LessonAttempt = { ...savedLessonResult, status: 'S' };
      //* A portfolio created, so the status is 'S(tarted)'.
      doLaunchLesson(lessonkey, lessonVersion, newAttempt);
    }
  }, [savedLessonResult]);

  useEffect(() => {
    if (saveLessonResultError) {
      setCreateNewAttemptError(true);
    }
  }, [saveLessonResultError]);

  useEffect(() => {
    if (queryParams?.searchParam) {
      setShowKewordSearch(queryParams.searchParam);
    }
  }, [queryParams]);

  const handleCloseOpenLessonError = () => {
    setCreateNewAttemptError(false);
  };

  const doLaunchLesson = (
    lessonkey: number,
    lessonVersion: number,
    portfolioAttempt: LessonAttempt,
    portfolioLocked: boolean = false
  ) => {
    const lesson = cloneDeep(
      courseLessons.find((l) => l.lessonkey === lessonkey)
    );
    const {
      type,
      title,
      description,
      displaySettings,
      completionType,
      completionThreshold,
      required,
      timeToComplete
    } = lesson;
    const lessonWindow = type === 'xapi' ? window.open() : null;
    const { state, completed, layer, status } = portfolioAttempt;
    const currentLessonIndex = courseLessons.findIndex(
      (l) => l.lessonkey === lesson.lessonkey
    );
    const launchParamsV2: LessonLaunchParamsV2 = {
      lessonWindow,
      title,
      description,
      lessonkey,
      lessonVersion,
      lessonType: type,
      displaySettings,
      state,
      status,
      completedDate: completed,
      layer,
      completionType,
      completionThreshold,
      portfolioLocked,
      hasCert: lesson.cert,
      required,
      lastLesson: courseLessons.length - 1 === currentLessonIndex,
      timeToComplete
    };
    setLesson(lesson);
    setLessonLaunchParamsV2(launchParamsV2);
  };

  const onSearch = (searchText: string) => setSearchText(searchText);
  const onFilter = (filterBy: ProgressStatusCode[]) => {
    setStatusFilter(filterBy);
  };

  const handleClose = () => {
    props.onClose();
  };

  const handleCloseMessage = (event?: MessageEvent) => {
    if (!event || event.data === 'lessonComplete') {
      setLessonLaunchParamsV2(null);
      handleMessage();
    }
  };

  function handleCloseLesson() {
    // Clear user's answer cache when closing the lesson. This will avoid
    // flickering user answers when immediately reload.
    cache.delete(answerUrl);
    setLessonLaunchParamsV2(null);
    refetchProgress();
  }

  const handleCloseLessonPlayer = () => {
    setLesson(null);
    handleCloseLesson();
  };

  const handleNewAttempt = (lesson: CourseLessonVm) => {
    const { lessonVersion, lessonkey } = lesson;
    upsertLessonResult(lessonkey, lessonVersion);
  };

  const continueCurrentAttempt = (portfolioAttempt: LessonAttempt) => {
    if (portfolioAttempt) {
      doLaunchLesson(
        portfolioAttempt.modulekey,
        portfolioAttempt.moduleversion,
        portfolioAttempt,
        [ApiPortfolioState.QuizLock, ApiPortfolioState.AssessmentLock].includes(
          portfolioAttempt.state
        )
      );
    } else {
      setCreateNewAttemptError(true);
    }
  };

  const handleRestart = () => {
    setOpen(false);
    handleNewAttempt(lesson);
  };

  const handleReview = () => {
    setOpen(false);
    const { lessonkey, latestPortfolioAttempt } = lesson;
    doLaunchLesson(
      +lessonkey,
      latestPortfolioAttempt.moduleversion,
      latestPortfolioAttempt,
      true
    );
  };

  const handleContinue = async () => {
    setOpen(false);
    const {
      lessonkey,
      lessonVersion,
      latestPortfolioAttempt,
      newVersionAvailable
    } = lesson;
    if (newVersionAvailable) {
      const migratedResult = await migratePortfolio(
        userkey,
        +props.pathway.pathkey,
        lessonkey,
        latestPortfolioAttempt.moduleversion,
        latestPortfolioAttempt.layer
      );
      if (migratedResult) {
        const migratedAttempt: LessonAttempt = {
          ...migratedResult.migratePortfolio,
          status: 'S'
        };
        doLaunchLesson(lessonkey, lessonVersion, migratedAttempt);
      }
    } else {
      continueCurrentAttempt(latestPortfolioAttempt);
    }
  };

  const handleLessonFlowClose = () => {
    setLesson(undefined);
    setOpen(false);
  };

  const handleLessonClick = (
    lesson: CourseLessonVm,
    oldAttempt?: LessonAttempt
  ) => {
    if (oldAttempt) {
      const { modulekey, moduleversion } = oldAttempt;
      doLaunchLesson(
        modulekey,
        moduleversion,
        oldAttempt,
        true /* Read-only mode for the finalised assessment old attempt. */
      );
    } else {
      const {
        latestPortfolioAttempt,
        allowRestart,
        newVersionAvailable,
        type
      } = lesson;
      if (!latestPortfolioAttempt || latestPortfolioAttempt.status === 'N') {
        handleNewAttempt(lesson);
      } else if (
        // B2-1561 Exceptional rule for `assessment` lessons:
        // Learners shouldn't be able to reattempt passed or failed formal assessments,
        // unless a new lesson version has been published or their certificate has expired.
        SharedLessonService.isNative(type) &&
        (latestPortfolioAttempt.status === 'E' ||
          (![
            ApiPortfolioState.AssessmentSubmit,
            ApiPortfolioState.AssessmentMark,
            ApiPortfolioState.AssessmentLock,
            ApiPortfolioState.AssessmentRetry
          ].includes(latestPortfolioAttempt.state) &&
            (allowRestart || newVersionAvailable)))
      ) {
        setLesson(lesson);
        setOpen(true);
      } else {
        continueCurrentAttempt(latestPortfolioAttempt);
      }
    }
  };

  const handleContinueToNextLesson = async (lessonCompleted: boolean) => {
    cache.delete(answerUrl);
    setNextLessonLoading(true);
    await refetchProgress();
    setNextLessonLoading(false);
    const currentLessonIndex = courseLessons.findIndex(
      (l) =>
        l.lessonkey === (lesson?.lessonkey || lessonLaunchParamsV2?.lessonkey)
    );
    const nextLesson = courseLessons[currentLessonIndex + 1];
    if (nextLesson) {
      if (
        courseLessons[currentLessonIndex].required &&
        !lessonCompleted && // Current lesson completed status.
        nextLesson.latestPortfolioAttempt?.status === 'L'
      ) {
        setLessonLaunchParamsV2(null);
      } else {
        handleLessonClick(nextLesson);
      }
    }
  };

  if (!courseLoading && loadCourseError) {
    return (
      <AlertDialog
        title="Whoops!"
        message="There was a problem with loading lessons."
        details={SUPPORT_MESSAGE}
        onClose={props.onClose}
      />
    );
  }

  return (
    <LessonPlayerContextProvider
      value={{
        preview: false,
        coursekey: +props.pathway.pathkey,
        lessonState
      }}
    >
      <Dialog
        fullScreen
        className={styles.courseDialog}
        open={props.open}
        TransitionComponent={Transition}
      >
        <ContentAppBar
          content={props.pathway}
          handleClose={handleClose}
          disable={lessonLaunchParamsV2 !== null}
          label="COURSE"
        />
        <div className={styles.lessonListFrame} style={backgroundStyle}>
          {courseLoading && <CustomLoadingProgress />}
          <div className={styles.lessonListBody}>
            {showKeywordSearch && (
              <>
                <KeywordLocate searchString={showKeywordSearch} />
                <Divider style={{ margin: '16px 0px' }} />
              </>
            )}

            <Box m={1}>
              <RecentLessonPanel
                loading={courseLoading}
                courseLessons={course?.recentLessons ?? []}
                contentType="course"
                onLaunchCourseLesson={handleLessonClick}
              />
            </Box>
            <Divider style={{ margin: '16px 0px' }} />
            <>
              <Box marginBottom={'12px'}>
                <Grid container justifyContent="space-between">
                  <Grid>
                    <SearchTextField onSearch={onSearch} />
                  </Grid>
                  <Grid>
                    <StatusPicker
                      options={Constants.StatusFilterOptions}
                      onChange={onFilter}
                    />
                  </Grid>
                </Grid>
              </Box>
              {lessons.length === 0 &&
              (!!searchText || !isEmpty(statusFilter)) ? (
                <NoSearchResultsFound />
              ) : (
                <LessonListPanel
                  loading={courseLoading}
                  title={props.pathway.title}
                  contentType="course"
                  courseLessons={lessons}
                  filteredByStatus={!isEmpty(statusFilter)}
                  onLaunchCourseLesson={handleLessonClick}
                />
              )}
            </>
          </div>
        </div>
      </Dialog>
      {createNewAttemptError && (
        <AlertDialog
          title="Open Lesson"
          message="There was a problem with getting your lesson."
          onClose={handleCloseOpenLessonError}
        />
      )}
      {open && (
        <LessonFlowDialog
          loading={courseLoading}
          lesson={lesson}
          handleClose={handleLessonFlowClose}
          handleContinue={handleContinue}
          handleRestart={handleRestart}
          handleReview={handleReview}
        />
      )}
      {lessonLaunchParamsV2 && (
        <LessonPlayerEmbedded
          launchParamsV2={lessonLaunchParamsV2}
          preview={false}
          coursekey={+props.pathway.pathkey}
          onMessage={handleCloseMessage}
          onCourseUpdate={() => refetchProgress()}
          onClose={handleCloseLessonPlayer}
          onContinueNextLesson={handleContinueToNextLesson}
        /> //* for Bracken2 lessons
      )}
      {(nextLessonLoading || savingLessonResult || migrating) && (
        <Overlay spinner message="Please wait..." />
      )}
    </LessonPlayerContextProvider>
  );
};

export default CourseDialog;
